import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import FileDropZone from '../components/commun/FileDropZone';
import { API_BASE_URL } from '../config/api';
import { isLegrandUser } from '../utils/userUtils';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
}

const FormCameroun: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // Récupération des informations depuis le state
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Dates
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');

  // Liste et sélection de voyageurs
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');

  // État de formulaire
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  // Assurance
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  // Chargement
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Pièces jointes (fichiers)
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [carnetVaccinationFile, setCarnetVaccinationFile] = useState<File | null>(null);
  const [acteNaissanceFile, setActeNaissanceFile] = useState<File | null>(null);
  const [justificatifDomicileFile, setJustificatifDomicileFile] = useState<File | null>(null);

  // Champs spécifiques aux affaires
  const [lettreInvitationFile, setLettreInvitationFile] = useState<File | null>(null);
  const [lettreMissionFile, setLettreMissionFile] = useState<File | null>(null);

  // Autres pièces financières / hébergement
  const [releveCompteFile, setReleveCompteFile] = useState<File | null>(null);
  const [fichePayeFile, setFichePayeFile] = useState<File | null>(null);
  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);
  const [livretFamilleFile, setLivretFamilleFile] = useState<File | null>(null);

  // Total
  const total = prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Formatage date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, ''); 
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);

        // Sélection automatique s'il n'y a qu'un seul voyageur
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l’onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Validation du formulaire
  const validateForm = () => {
    const errors: string[] = [];

    // Conversions de date
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    // Vérification date départ
    if (!dateDepart.trim()) {
      errors.push('Date de départ');
    } else if (departDate && (isNaN(departDate.getTime()) || departDate <= today)) {
      errors.push("La date de départ doit être postérieure à aujourd'hui.");
    }

    // Vérification date retour
    if (!dateSortie.trim()) {
      errors.push('Date de sortie');
    } else if (sortieDate && departDate && sortieDate <= departDate) {
      errors.push('La date de retour doit être postérieure à la date de départ.');
    }

    // Sélection voyageur
    if (!selectedVoyageur) errors.push('Voyageur non sélectionné');

    // Pièces obligatoires
    if (!billetAvionFile) errors.push('Billet d’avion aller-retour nominatif requis');
    if (!carnetVaccinationFile) errors.push('Carnet de vaccination fièvre jaune requis');
    if (!justificatifDomicileFile) errors.push('Justificatif de domicile requis');

    // Acte de naissance : non demandé pour un visa affaires
    if (!title.toLowerCase().includes('affaire') && !acteNaissanceFile) {
      errors.push('Acte de naissance ou livret de famille requis');
    }

    // Si visa affaire
    if (title.toLowerCase().includes('affaire')) {
      if (!lettreInvitationFile) errors.push("Lettre d'invitation (affaires) requise");
      if (!lettreMissionFile) errors.push("Lettre de mission (affaires) requise");
    }

    // Documents financiers - Requis seulement si ce n'est PAS VIP ou affaire
    const isVipOrAffaire = title.toUpperCase().includes('VIP') || title.toLowerCase().includes('affaire');
    if (!isVipOrAffaire) {
      if (!releveCompteFile) errors.push('Dernier relevé de compte bancaire requis');
      if (!fichePayeFile) errors.push('Dernière fiche de paye requise');
      if (!livretFamilleFile) errors.push('Livret de famille requis');
    }

    // Assurance
    if (!assuranceVisa) errors.push('Assurance Visa');
    if (!sliderValue) errors.push("Engagement sur les conditions générales de vente");

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    selectedVoyageur,
    billetAvionFile,
    carnetVaccinationFile,
    acteNaissanceFile,
    justificatifDomicileFile,
    lettreInvitationFile,
    lettreMissionFile,
    releveCompteFile,
    fichePayeFile,
    reservationHotelFile,
    livretFamilleFile,
    assuranceVisa,
    sliderValue,
  ]);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(
        'Les informations saisies ne sont pas enregistrées. Êtes-vous sûr de quitter ce formulaire ?'
      )
    ) {
      navigate(-1);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);

      // Ajout des fichiers
      if (billetAvionFile) formData.append('billetAvionFile', billetAvionFile);
      if (carnetVaccinationFile) formData.append('carnetVaccinationFile', carnetVaccinationFile);
      if (!title.toLowerCase().includes('affaire') && acteNaissanceFile) {
        formData.append('acteNaissanceFile', acteNaissanceFile);
      }
      if (justificatifDomicileFile) {
        formData.append('justificatifDomicileFile', justificatifDomicileFile);
      }

      // Si affaires
      if (title.toLowerCase().includes('affaire')) {
        if (lettreInvitationFile) formData.append('lettreInvitationFile', lettreInvitationFile);
        if (lettreMissionFile) formData.append('lettreMissionFile', lettreMissionFile);
      }

      // Vérifier si VIP ou affaire
      const isVipOrAffaire = title.toUpperCase().includes('VIP') || title.toLowerCase().includes('affaire');

      // Autres pièces - Seulement si ce n'est PAS VIP ou affaire
      if (!isVipOrAffaire) {
        if (releveCompteFile) formData.append('releveCompteFile', releveCompteFile);
        if (fichePayeFile) formData.append('fichePayeFile', fichePayeFile);
        if (livretFamilleFile) formData.append('livretFamilleFile', livretFamilleFile);
      }
      // Réservation d'hôtel est toujours envoyée si elle existe
      if (reservationHotelFile) formData.append('reservationHotelFile', reservationHotelFile);

      // Envoi vers le backend
      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setBilletAvionFile(null);
    setCarnetVaccinationFile(null);
    setActeNaissanceFile(null);
    setJustificatifDomicileFile(null);
    setLettreInvitationFile(null);
    setLettreMissionFile(null);
    setReleveCompteFile(null);
    setFichePayeFile(null);
    setReservationHotelFile(null);
    setLivretFamilleFile(null);
  };

  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistrée avec succès !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {title} a bien été enregistrée.
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Créer une nouvelle demande
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 3,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {/* En-tête */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              Formulaire pour {title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Retour
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">Sélectionner un voyageur</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label="Sélectionner un voyageur"
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((v) => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? ` - Passeport: ${v.numeropax}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Créer un nouveau voyageur
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt="Avatar"
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Dates */}
          <TextField
            label="Date de départ (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date de retour (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Billet d’avion */}
          <FileDropZone
            label="Billet d’avion aller-retour nominatif"
            file={billetAvionFile}
            onFileSelected={setBilletAvionFile}
            required
          />

          {/* Carnet de vaccination fièvre jaune */}
          <FileDropZone
            label="Carnet de vaccination fièvre jaune"
            file={carnetVaccinationFile}
            onFileSelected={setCarnetVaccinationFile}
            required
          />

          {/* Acte de naissance ou livret de famille - Masqué si Affaires */}
          {!title.toLowerCase().includes('affaire') && (
            <FileDropZone
              label="Acte de naissance ou livret de famille"
              file={acteNaissanceFile}
              onFileSelected={setActeNaissanceFile}
              required
            />
          )}

          {/* Justificatif de domicile */}
          <FileDropZone
            label="Justificatif de domicile"
            file={justificatifDomicileFile}
            onFileSelected={setJustificatifDomicileFile}
            required
          />

          {/* Si affaires : Lettres invitation / mission */}
          {title.toLowerCase().includes('affaire') && (
            <>
              <FileDropZone
                label="Lettre d’invitation (affaires)"
                file={lettreInvitationFile}
                onFileSelected={setLettreInvitationFile}
                required
              />
              <FileDropZone
                label="Lettre de mission (affaires)"
                file={lettreMissionFile}
                onFileSelected={setLettreMissionFile}
                required
              />
            </>
          )}

          {/* Relevé de compte - Masqué si VIP ou Affaires */}
          {!title.toUpperCase().includes('VIP') && !title.toLowerCase().includes('affaire') && (
            <FileDropZone
              label="Dernier relevé de compte bancaire"
              file={releveCompteFile}
              onFileSelected={setReleveCompteFile}
              required
            />
          )}

          {/* Fiche de paye - Masqué si VIP ou Affaires */}
          {!title.toUpperCase().includes('VIP') && !title.toLowerCase().includes('affaire') && (
            <FileDropZone
              label="Dernière fiche de paye"
              file={fichePayeFile}
              onFileSelected={setFichePayeFile}
              required
            />
          )}

          {/* Réservation d'hôtel */}
          <FileDropZone
            label="Réservation d'hôtel ou certificat d'hébergement visé par la Mairie"
            aide="(facultatif selon votre cas)"
            file={reservationHotelFile}
            onFileSelected={setReservationHotelFile}
          />

          {/* Livret de famille (si époux/enfants de personne née au Cameroun) - Masqué si VIP ou Affaires */}
          {!title.toUpperCase().includes('VIP') && !title.toLowerCase().includes('affaire') && (
            <FileDropZone
              label="Livret de famille"
              aide="(uniquement pour les époux/épouses et enfants des personnes nées au Cameroun)"
              file={livretFamilleFile}
              onFileSelected={setLivretFamilleFile}
              required
            />
          )}

          {/* Module Assurance */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFeesForLegrand && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">Frais de visa : {prixconsulat}€</Typography>
              <Typography variant="body1">Frais de service : {prixintervention}€</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total : {total}€
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  Formulaire incomplet.
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">Formulaire complet</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                Valider ma demande
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormCameroun;
